/**
 * Live price / NAV / AUM summary for a single fund.
 *
 * Fetches from `GET /api/data/funds/:ticker/summary` (Postgres-backed,
 * Cache-Control: no-store, ~5 min freshness in practice). Populates NAV and
 * AUM from the latest ingested daily-NAV row; market price, change, spread,
 * and premium/discount remain null until a Twelve Data stream is wired.
 */
import { apiFetch } from "@/lib/api-client";
import type { Fund } from "@etfsite/db/types";
import { useQuery } from "@tanstack/react-query";

export type LiveDataSource = "live" | "nav" | "none";

export interface FundLiveData {
  price: number | null;
  nav: number | null;
  aum: number | null;
  change: number | null;
  changePct: number | null;
  premiumDiscountPct: number | null;
  medianSpreadPct: number | null;
  asOfDate: string | null;
  navAsOfDate: string | null;
  source: LiveDataSource;
}

interface FundSummaryResponse {
  ticker: string;
  latest_nav: string | null;
  latest_nav_date: string | null;
  latest_aum: string | null;
  latest_holdings_date: string | null;
  holdings_count: number;
  fund_exists?: boolean;
}

export const EMPTY: FundLiveData = {
  price: null,
  nav: null,
  aum: null,
  change: null,
  changePct: null,
  premiumDiscountPct: null,
  medianSpreadPct: null,
  asOfDate: null,
  navAsOfDate: null,
  source: "none",
};

function parseSummary(r: FundSummaryResponse): FundLiveData {
  const nav = r.latest_nav != null ? Number(r.latest_nav) : null;
  const aum = r.latest_aum != null ? Number(r.latest_aum) : null;
  return {
    ...EMPTY,
    nav: Number.isFinite(nav) ? nav : null,
    aum: Number.isFinite(aum) ? aum : null,
    navAsOfDate: r.latest_nav_date ?? null,
    source: nav != null ? "nav" : "none",
  };
}

export function useFundLiveData(ticker: string | null | undefined): FundLiveData | null {
  const { data, isError } = useQuery({
    queryKey: ["fund-summary", ticker],
    queryFn: () =>
      apiFetch<FundSummaryResponse>(`/api/data/funds/${ticker?.toUpperCase()}/summary`),
    enabled: Boolean(ticker),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Return null on error so callers can distinguish "no data yet" from "loading".
  // In practice the backend always returns 200 with nulls when ingestion hasn't
  // run yet, so isError only fires on genuine network/server failures.
  if (isError) return null;
  return data ? parseSummary(data) : EMPTY;
}

// ── Formatting helpers (unchanged — kept alongside the hook for co-location) ──

export function formatPrice(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `$${value.toFixed(2)}`;
}

export function formatChange(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(value).toFixed(2)}`;
}

export function formatChangePct(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
}

export function formatSpreadPct(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)}%`;
}

export function formatAum(value: number | null): string {
  if (value == null || !Number.isFinite(value) || value <= 0) return "—";
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 950_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

/**
 * Latest live AUM (total net assets) in dollars from the daily_nav overlay on
 * the funds-list response, or null when the fund has no NAV row. Callers fall
 * back to the static DDB `aum` display string when this returns null.
 */
export function fundLiveAumDollars(fund: Fund): number | null {
  if (fund.latestNetAssets == null) return null;
  const n = Number(fund.latestNetAssets);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Legacy name: previously output "M/D/YYYY" (e.g. "1/5/2025") — now outputs
// "MMM D, YYYY" (e.g. "Jan 5, 2025") to match Dane's live site and the
// fund-detail pages (formatAsOfDate / formatFactAsOf). Rename to formatAsOfLong
// in a future cleanup; keep the name now to avoid rippling to multiple callers.
export function formatAsOfMdy(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const isIso10 = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const candidate = isIso10 ? `${iso}T12:00:00` : iso;
  const d = new Date(candidate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/New_York",
  });
}
